<template>
  <DefaultAuthLayout>
    <!-- Top Action Slot -->
    <template #topAction>
      <span class="text-gray mr-3 lg:inline">Lembrou da senha?</span>
      <router-link to="/login" class="border border-stroke px-3 py-1 text-sm text-body transition-colors hover:bg-gray-2 lg:px-6 lg:py-2 lg:text-base">
        Voltar ao Login
      </router-link>
    </template>

    <!-- Header Slot -->
    <template #header>
      <h2 class="mb-2 text-2xl font-bold text-black lg:text-3xl">Redefinir senha</h2>
      <p class="text-sm text-body lg:text-base">Crie uma nova senha para sua conta.</p>
    </template>

    <!-- Form Slot -->
    <template #form>
      <form @submit.prevent="handleSubmit" class="space-y-4 lg:space-y-6">
        <!-- Temporary Password Field — visivel apenas quando o usuario chegou
             aqui sem ter feito o fluxo via login (fallback manual). -->
        <div v-if="!hasTemporaryPasswordFromStore" class="mb-3 lg:mb-4">
          <label class="mb-2 block text-xs font-medium uppercase tracking-wide text-black lg:text-sm">
            {{ t('views.update_password.label.temporary_password') }}
          </label>
          <div class="relative">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 lg:pl-4">
              <IcKey />
            </div>
            <input
              :type="showTempPassword ? 'text' : 'password'"
              name="temporary_password"
              v-model="form.temporary_password"
              placeholder="Digite sua senha temporária"
              class="w-full border border-stroke bg-transparent py-2.5 pl-10 pr-10 text-sm text-black outline-none transition-colors focus:border-blue focus:ring-2 focus:ring-blue lg:py-3 lg:pl-12 lg:pr-12 lg:text-base" />
            <button type="button" @click="showTempPassword = !showTempPassword" class="absolute inset-y-0 right-0 flex items-center pr-3 lg:pr-4">
              <IcEyeOff v-if="showTempPassword" />
              <IcEye v-else />
            </button>
          </div>
          <p v-if="errors.temporary_password" class="mt-1 text-xs text-danger">{{ errors.temporary_password }}</p>
        </div>

        <!-- New Password Field -->
        <div class="mb-3 lg:mb-4">
          <label class="mb-2 block text-xs font-medium uppercase tracking-wide text-black lg:text-sm">
            {{ t('views.update_password.label.new_password') }}
          </label>
          <div class="relative">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 lg:pl-4">
              <IcLock />
            </div>
            <input
              :type="showNewPassword ? 'text' : 'password'"
              name="new_password"
              v-model="form.new_password"
              placeholder="Digite sua nova senha"
              class="w-full border border-stroke bg-transparent py-2.5 pl-10 pr-10 text-sm text-black outline-none transition-colors focus:border-blue focus:ring-2 focus:ring-blue lg:py-3 lg:pl-12 lg:pr-12 lg:text-base" />
            <button type="button" @click="showNewPassword = !showNewPassword" class="absolute inset-y-0 right-0 flex items-center pr-3 lg:pr-4">
              <IcEyeOff v-if="showNewPassword" />
              <IcEye v-else />
            </button>
          </div>
          <p v-if="errors.new_password" class="mt-1 text-xs text-danger">{{ errors.new_password }}</p>
        </div>

        <!-- Password Confirmation Field -->
        <div class="mb-3 lg:mb-4">
          <label class="mb-2 block text-xs font-medium uppercase tracking-wide text-black lg:text-sm">
            {{ t('views.update_password.label.password_confirmation') }}
          </label>
          <div class="relative">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 lg:pl-4">
              <IcCheck />
            </div>
            <input
              :type="showConfirmPassword ? 'text' : 'password'"
              name="password_confirmation"
              v-model="form.password_confirmation"
              placeholder="Confirme sua nova senha"
              class="w-full border border-stroke bg-transparent py-2.5 pl-10 pr-10 text-sm text-black outline-none transition-colors focus:border-blue focus:ring-2 focus:ring-blue lg:py-3 lg:pl-12 lg:pr-12 lg:text-base" />
            <button type="button" @click="showConfirmPassword = !showConfirmPassword" class="absolute inset-y-0 right-0 flex items-center pr-3 lg:pr-4">
              <IcEyeOff v-if="showConfirmPassword" />
              <IcEye v-else />
            </button>
          </div>
          <p v-if="errors.password_confirmation" class="mt-1 text-xs text-danger">{{ errors.password_confirmation }}</p>
        </div>

        <!-- Submit Button -->
        <div class="mb-3 mt-6 lg:mb-5 lg:mt-8">
          <button
            @click="handleSubmit"
            :disabled="isLoading"
            class="flex w-full cursor-pointer justify-center border border-primary bg-primary p-3 font-medium text-white transition hover:bg-blue disabled:cursor-not-allowed disabled:opacity-50 lg:p-4">
            <div class="flex flex-row items-center text-center">
              <div v-if="isLoading" class="mr-3 animate-spin items-center">
                <svg class="h-5 w-5 text-white lg:h-7 lg:w-7" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <span class="flex items-center justify-center text-base font-medium lg:text-lg">
                {{ isLoading ? 'Atualizando...' : t('views.update_password.action.submit') }}
              </span>
            </div>
          </button>
        </div>
      </form>
    </template>
  </DefaultAuthLayout>
</template>

<script setup lang="ts">
import DefaultAuthLayout from '@/components/Auths/DefaultAuthLayout.vue'
import { IcCheck, IcEye, IcEyeOff, IcKey, IcLock } from '@/components/Icons'
import { IUpdatePassword } from '@/interfaces/stores/user'
import UpdatePasswordSchema from '@/schemas/UpdatePasswordSchema'
import { useUserStore } from '@/store/user'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { ValidationError } from 'yup'

const { t } = useI18n()
const router = useRouter()
const userStore = useUserStore()
const { updatePassword, clearTemporaryPassword } = userStore

const isLoading = ref(false)
const showTempPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)

// Senha temporaria pode ter sido fornecida automaticamente pelo fluxo de
// login (quando backend retornou requires_password_change). Nesse caso o
// campo nao aparece na UI e o usuario so digita a senha nova + confirmacao.
const hasTemporaryPasswordFromStore = computed(() => userStore.temporaryPassword.length > 0)

const form = ref({
  temporary_password: userStore.temporaryPassword,
  new_password: '',
  password_confirmation: '',
})

const errors = ref({
  temporary_password: '',
  new_password: '',
  password_confirmation: '',
})

const setErrors = (validationErrors: ValidationError) => {
  errors.value = {
    temporary_password: '',
    new_password: '',
    password_confirmation: '',
  }

  validationErrors.inner.forEach((error) => {
    if (error.path) {
      errors.value[error.path as keyof typeof errors.value] = error.message
    }
  })
}

const doUpdatePassword = (values: IUpdatePassword) => {
  isLoading.value = true

  updatePassword(values as IUpdatePassword, {
    onSuccess: () => {
      // Limpa a senha temporaria do store apos uso. O usuario volta para o
      // login para entrar com a nova senha (mesmo padrao do bem_app).
      clearTemporaryPassword()
      isLoading.value = false
      router.push({ name: 'login' })
    },
    onFailure: () => {
      isLoading.value = false
    },
    onCompletion: () => {
      isLoading.value = false
    },
  })
}

const handleSubmit = () => {
  UpdatePasswordSchema.validate(form.value, { abortEarly: false })
    .then(() => {
      doUpdatePassword(form.value)
    })
    .catch((err: ValidationError) => {
      setErrors(err)
    })
}
</script>
